import React from 'react'
import classNames from 'classnames'
import { TITLE as TITLE_TYPE_ID } from 'constants/app.constant'
import useResponsive from 'utils/hooks/useResponsive'

// Wrapper compartido del cuestionario activo. Renderiza:
//  - Card con header (nombre del cuestionario + pills de metadata opcionales)
//  - Lista de preguntas separadas por border-t, TITLE como bloque destacado dentro del card
//  - Slot footer opcional (CTA o lo que el consumidor pase)
//
// Centraliza el iterador TITLE-aware para que TicketQuestion, TicketQuestionHome,
// AddTicket y QuestionGroupPreview no dupliquen logica.
//
// NOTA: usamos border-t en cada <li> en lugar de "divide-y" en el <ul>.
// "divide-y" de Tailwind requiere que el watcher detecte la clase al arrancar el server.
// Como este archivo es nuevo y el watcher no la incluyo en el build inicial, los
// separadores no se pintaban. border-t es una clase base que siempre esta en el bundle.
const QuestionnaireWrapper = ({
    name,
    pills,
    headerNotice,
    questions = [],
    renderQuestion,
    footer,
    footerSticky,
    className,
    nesting,
    nestingMode = 'fija',
}) => {
    const { smaller } = useResponsive()
    // El sticky bottom solo aplica en mobile/tablet. En desktop el footer
    // queda en flujo normal porque hay altura natural sin overflow problematico.
    const stickyOnMobile = footerSticky && smaller.md
    // Si el cuestionario tiene algun TITLE, las preguntas se indentan
    // (pl-6) para crear jerarquia visual TITLE > pregunta.
    const hasAnyTitle = questions.some(q => q.question_type_id === TITLE_TYPE_ID)
    // Sangria de una pregunta condicional respecto de su padre. En mobile es
    // chica a proposito: a 375 px, tres niveles de sangria completa dejan el
    // texto sin ancho util.
    const sangriaHija = smaller.md ? 'pl-2.5' : 'pl-7'
    // El numero jerarquico (1.1.1) no se dibuja. Vivia fuera del flujo, en el padding
    // izquierdo del `<li>`, y ese aire prestado no le alcanzaba en todos los casos: se
    // partia en dos renglones o se montaba sobre el enunciado apenas el contenedor se
    // angostaba, la sangria sumaba un nivel o el numero pasaba a tres digitos. Ensancharle
    // el padding en desktop tapo el caso que se habia reportado, no la causa. Alexis lo bajo
    // el 2026-09-10: "le quitemos los numeros en la prueba y en el cuestionario respondido,
    // porque veo qeu se rompen o cuando se esta respondiendo tambien".
    //
    // `nesting` se sigue recibiendo y usando: de ahi salen el `nivel`, la sangria y la barra
    // indigo, que son las que sostienen la jerarquia ahora.
    const padX = hasAnyTitle ? 'px-6' : 'px-4'
    return (
        <>
        <div className={className}>
            {(name || (pills && pills.length > 0) || headerNotice) && (
                <div className={classNames(padX, 'py-3 border-b border-gray-200 dark:border-gray-700')}>
                    {/* Stack vertical: titulo arriba, pills debajo. */}
                    {name && (
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            {name}
                        </h3>
                    )}
                    {pills && pills.length > 0 && (
                        <div className={classNames('flex flex-wrap gap-2', name && 'mt-2')}>
                            {pills.map((pill, idx) => {
                                let toneClass
                                if (pill.tone === 'amber') {
                                    // Border en amber-400 (amarillo claro, no marron)
                                    // para que se vea como pill amarillo y no como
                                    // pildora con borde marron oscuro.
                                    toneClass = 'bg-amber-50 text-amber-800 border border-amber-400 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-400/60'
                                } else if (pill.tone === 'emerald') {
                                    toneClass = 'bg-emerald-50 text-emerald-800 border border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-400/60'
                                } else {
                                    toneClass = 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                                }
                                return (
                                    <span
                                        key={pill.key ?? idx}
                                        className={classNames(
                                            'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                                            toneClass,
                                        )}
                                    >
                                        {pill.icon}
                                        {pill.label}
                                    </span>
                                )
                            })}
                        </div>
                    )}
                    {/* Banner u otro aviso accionable debajo de los pills */}
                    {headerNotice && (
                        <div className="mt-2">{headerNotice}</div>
                    )}
                </div>
            )}

            <ul aria-label={name || 'Cuestionario'}>
                {questions.map((q, i) => {
                    const isTitle = q.question_type_id === TITLE_TYPE_ID
                    const prev = questions[i - 1]
                    const prevWasTitle = prev?.question_type_id === TITLE_TYPE_ID
                    const meta = nesting ? nesting[String(q.id)] : undefined
                    const nivel = meta?.nivel ?? 0

                    if (isTitle) {
                        return (
                            <li
                                key={q.id}
                                className={classNames(
                                    padX,
                                    'pb-2',
                                    // Linea violeta (indigo-200) en el mismo tono del
                                    // titulo de seccion, para que se lea como una unidad
                                    // visual. pt-5 da aire entre linea y texto.
                                    i === 0
                                        ? 'pt-4'
                                        : 'pt-5 mt-3 border-t border-indigo-100 dark:border-indigo-900/50',
                                )}
                            >
                                {renderQuestion(q, i, meta)}
                            </li>
                        )
                    }

                    const contenido = renderQuestion(q, i, meta)

                    return (
                        <li
                            key={q.id}
                            className={classNames(
                                'transition-colors hover:bg-indigo-50/50 dark:hover:bg-indigo-950/20',
                                // Simetrico: la sangria bajo un TITLE corre la pregunta de
                                // los dos lados, no solo del izquierdo. Con `pr-4` fijo, el
                                // control quedaba 8 px mas cerca del borde derecho que del
                                // izquierdo, y eso se sumaba a los ~15 px que le comia el
                                // numero cuando estaba en columna.
                                padX,
                                // Una hija va pegada a su padre: sin separador arriba y con
                                // menos aire, para que el bloque se lea como una unidad. La
                                // proxima raiz recupera el py-4 y su border-t.
                                nivel > 0 ? 'pt-2 pb-2' : 'py-4',
                                // border-t para separar preguntas, excepto:
                                //  - primer item del card
                                //  - despues de un TITLE (ya separa la nueva seccion)
                                //  - preguntas condicionales (cuelgan de la de arriba)
                                // gray-300 (no gray-200) para que el limite entre preguntas
                                // se distinga con claridad (pedido de Sabri, ver plan.md).
                                i === 0 || prevWasTitle || nivel > 0
                                    ? ''
                                    : 'border-t border-gray-300 dark:border-gray-600',
                            )}
                        >
                            {nivel > 0
                                ? (
                                    <div
                                        className={classNames(
                                            sangriaHija,
                                            'border-l-2 border-indigo-200 dark:border-indigo-800/70',
                                        )}
                                        // 'fija' corre la rama entera una sola vez, sin importar
                                        // la profundidad: el ancho de texto queda constante y el
                                        // numero carga el nivel. 'acumulativa' sangra por nivel,
                                        // para leer en desktop donde no hay presion de ancho.
                                        style={nestingMode === 'acumulativa' && nivel > 1
                                            ? { marginLeft: (nivel - 1) * 16 }
                                            : undefined
                                        }
                                    >
                                        {contenido}
                                    </div>
                                )
                                : contenido
                            }
                        </li>
                    )
                })}
            </ul>
        </div>
        {footer && (
            <div
                className={classNames(
                    !stickyOnMobile && 'mt-4',
                    // fixed (no sticky): igual que TicketPrimaryActions, sticky
                    // se anclaba al contenedor padre y quedaba al final del
                    // scroll, no al fondo del viewport. fixed garantiza visibilidad
                    // al pie con bg/border/shadow para que se lea como action bar.
                    // paddingBottom inline porque el JIT puede no levantar el
                    // max(env()) arbitrario al hot-reload.
                    stickyOnMobile && 'fixed inset-x-0 bottom-0 z-30 px-4 pt-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]',
                )}
                style={stickyOnMobile
                    ? { paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }
                    : undefined
                }
            >
                {footer}
            </div>
        )}
        </>
    )
}

export default QuestionnaireWrapper
